import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import LoginPage from "./pages/auth/LoginPage";
import { AuthProvider, useAuth } from "./hooks/useAuth";
import { BrandingProvider } from "./contexts/BrandingContext";
import RecordingsPage from "./pages/RecordingsPage";
import CampaignsPage from "./pages/campaigns/CampaignsPage";
import OnboardingPage from "./pages/onboarding/OnboardingPage";
import DNITestPage from "./pages/DNITestPage";
import { ProfilePage } from "./pages/settings/ProfilePage";
import { IntegrationsPage } from "./pages/settings/Integrations";
import { SlackCallback } from "./pages/settings/integrations/SlackCallback";
import { HubSpotCallback } from "./pages/settings/integrations/HubSpotCallback";
import { GoogleCallback } from "./pages/settings/integrations/GoogleCallback";
import { useOnboardingState } from "./integrations/supabase/onboarding";
import TwilioSubaccountsPage from "./pages/admin/TwilioSubaccountsPage";
import UserAccountTypesPage from "./pages/admin/UserAccountTypesPage";
import SMSRateLimitsPage from "./pages/admin/SMSRateLimitsPage";
import BillingOverviewPage from "./pages/admin/billing/index";
import BillingPlansPage from "./pages/admin/billing/plans";
import BillingSettingsPage from "./pages/admin/billing/settings";
import CampaignDetailPage from "./pages/campaigns/CampaignDetailPage";
import CallsPage from "./pages/calls/CallsPage";
import CallDetailPage from "./pages/calls/CallDetailPage";
import TestingPage from "./pages/testing/TestingPage";
import MessagesPage from "./pages/messages/MessagesPage";
import AgencyBillingPage from "./pages/agency/billing/index";
import ClientBillingDetailPage from "./pages/agency/billing/ClientBillingDetailPage";
import InvoiceDetailPage from "./pages/agency/billing/InvoiceDetailPage";
import EncryptionKeyManagement from "./pages/admin/security/EncryptionKeyManagement";
import TenantsPage from "./pages/admin/TenantsPage";
import BrandingOverviewPage from "./pages/admin/BrandingOverviewPage";
import CustomersPage from "./pages/agency/customers/CustomersPage";
import NewCustomerPage from "./pages/agency/customers/NewCustomerPage";
import CustomerDetailPage from "./pages/agency/customers/CustomerDetailPage";
import BrandingPage from "./pages/agency/branding/BrandingPage";
import DomainVerificationPage from "./pages/agency/domain/DomainVerificationPage";
import PlatformSettingsPage from "./pages/admin/PlatformSettingsPage";

const queryClient = new QueryClient();

const PrivateRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
          <p>Loading your account...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth/login" replace />;
  }

  return children;
};

// New component that checks onboarding state and redirects if necessary
const OnboardingRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, isAgency, isBusinessOwner } = useAuth();
  const { onboarding, loading } = useOnboardingState();

  if (loading || !user) {
    return children;
  }

  // Only redirect if user is agency or business_owner
  if (
    (isAgency() || isBusinessOwner()) &&
    onboarding &&
    onboarding.status !== "completed" &&
    !window.location.pathname.includes("/onboarding")
  ) {
    return <Navigate to="/onboarding" replace />;
  }

  return children;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <BrandingProvider>
          <Routes>
            <Route path="/auth/login" element={<LoginPage />} />

            <Route
              path="/onboarding"
              element={
                <PrivateRoute>
                  <OnboardingPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/twilio-subaccounts"
              element={
                <PrivateRoute>
                  <OnboardingRoute>
                    <TwilioSubaccountsPage />
                  </OnboardingRoute>
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/user-account-types"
              element={
                <PrivateRoute>
                  <OnboardingRoute>
                    <UserAccountTypesPage />
                  </OnboardingRoute>
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/sms-rate-limits"
              element={
                <PrivateRoute>
                  <OnboardingRoute>
                    <SMSRateLimitsPage />
                  </OnboardingRoute>
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/billing"
              element={
                <PrivateRoute>
                  <OnboardingRoute>
                    <BillingOverviewPage />
                  </OnboardingRoute>
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/billing/plans"
              element={
                <PrivateRoute>
                  <OnboardingRoute>
                    <BillingPlansPage />
                  </OnboardingRoute>
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/billing/settings"
              element={
                <PrivateRoute>
                  <OnboardingRoute>
                    <BillingSettingsPage />
                  </OnboardingRoute>
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/security/encryption"
              element={
                <PrivateRoute>
                  <OnboardingRoute>
                    <EncryptionKeyManagement />
                  </OnboardingRoute>
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/tenants"
              element={
                <PrivateRoute>
                  <OnboardingRoute>
                    <TenantsPage />
                  </OnboardingRoute>
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/branding"
              element={
                <PrivateRoute>
                  <OnboardingRoute>
                    <BrandingOverviewPage />
                  </OnboardingRoute>
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/settings"
              element={
                <PrivateRoute>
                  <OnboardingRoute>
                    <PlatformSettingsPage />
                  </OnboardingRoute>
                </PrivateRoute>
              }
            />
            <Route
              path="/"
              element={
                <PrivateRoute>
                  <OnboardingRoute>
                    <Index />
                  </OnboardingRoute>
                </PrivateRoute>
              }
            />
            <Route
              path="/campaigns"
              element={
                <PrivateRoute>
                  <OnboardingRoute>
                    <CampaignsPage />
                  </OnboardingRoute>
                </PrivateRoute>
              }
            />
            <Route
              path="/campaigns/:id"
              element={
                <PrivateRoute>
                  <OnboardingRoute>
                    <CampaignDetailPage />
                  </OnboardingRoute>
                </PrivateRoute>
              }
            />
            <Route
              path="/calls"
              element={
                <PrivateRoute>
                  <OnboardingRoute>
                    <CallsPage />
                  </OnboardingRoute>
                </PrivateRoute>
              }
            />
            <Route
              path="/calls/:id"
              element={
                <PrivateRoute>
                  <OnboardingRoute>
                    <CallDetailPage />
                  </OnboardingRoute>
                </PrivateRoute>
              }
            />
            <Route
              path="/recordings"
              element={
                <PrivateRoute>
                  <OnboardingRoute>
                    <RecordingsPage />
                  </OnboardingRoute>
                </PrivateRoute>
              }
            />
            <Route
              path="/messages"
              element={
                <PrivateRoute>
                  <OnboardingRoute>
                    <MessagesPage />
                  </OnboardingRoute>
                </PrivateRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <PrivateRoute>
                  <OnboardingRoute>
                    <ProfilePage />
                  </OnboardingRoute>
                </PrivateRoute>
              }
            />
            <Route
              path="/settings/integrations"
              element={
                <PrivateRoute>
                  <OnboardingRoute>
                    <IntegrationsPage />
                  </OnboardingRoute>
                </PrivateRoute>
              }
            />
            <Route
              path="/settings/integrations/slack/callback"
              element={
                <PrivateRoute>
                  <SlackCallback />
                </PrivateRoute>
              }
            />
            <Route
              path="/settings/integrations/hubspot/callback"
              element={
                <PrivateRoute>
                  <HubSpotCallback />
                </PrivateRoute>
              }
            />
            <Route
              path="/settings/integrations/google/callback"
              element={
                <PrivateRoute>
                  <GoogleCallback />
                </PrivateRoute>
              }
            />
            <Route
              path="/dni-test"
              element={
                <PrivateRoute>
                  <DNITestPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/testing"
              element={
                <PrivateRoute>
                  <OnboardingRoute>
                    <TestingPage />
                  </OnboardingRoute>
                </PrivateRoute>
              }
            />
            <Route
              path="/agency/billing"
              element={
                <PrivateRoute>
                  <OnboardingRoute>
                    <AgencyBillingPage />
                  </OnboardingRoute>
                </PrivateRoute>
              }
            />
            <Route
              path="/agency/billing/clients/:id"
              element={
                <PrivateRoute>
                  <OnboardingRoute>
                    <ClientBillingDetailPage />
                  </OnboardingRoute>
                </PrivateRoute>
              }
            />
            <Route
              path="/agency/billing/invoices/:invoiceId"
              element={
                <PrivateRoute>
                  <OnboardingRoute>
                    <InvoiceDetailPage />
              </OnboardingRoute>
            </PrivateRoute>
          }
        />
            <Route
              path="/agency/customers"
              element={
                <PrivateRoute>
                  <OnboardingRoute>
                    <CustomersPage />
                  </OnboardingRoute>
                </PrivateRoute>
              }
            />
            <Route
              path="/agency/customers/new"
              element={
                <PrivateRoute>
                  <OnboardingRoute>
                    <NewCustomerPage />
                  </OnboardingRoute>
                </PrivateRoute>
              }
            />
            <Route
              path="/agency/customers/:id"
              element={
                <PrivateRoute>
                  <OnboardingRoute>
                    <CustomerDetailPage />
                  </OnboardingRoute>
                </PrivateRoute>
              }
            />
            <Route
              path="/agency/branding"
              element={
                <PrivateRoute>
                  <OnboardingRoute>
                    <BrandingPage />
                  </OnboardingRoute>
                </PrivateRoute>
              }
            />
            <Route
              path="/agency/domain"
              element={
                <PrivateRoute>
                  <OnboardingRoute>
                    <DomainVerificationPage />
                  </OnboardingRoute>
                </PrivateRoute>
              }
            />
            <Route path="*" element={<NotFound />} />
          </Routes>
          </BrandingProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
